// -----------------------------------------------------------------------------
// BatchImport — multi-image import (iter12; slot count dynamic in iter23)
// Layout: 1 mandatory list slot + N detail slots, where N = user's
// `max_tracked_per_day` setting (defaults to 10). User can multi-select images
// in one action; empty slots are auto-filled in order (first pick → list slot
// if empty, remaining → detail slots — max N+1 accepted).
// After extraction, list rows are truncated to top-N by rank so the app never
// creates more than the cap.
// -----------------------------------------------------------------------------
import { useState, useRef, useMemo, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ExtractionOverlay from "@/components/ExtractionOverlay";
import { DateTimeControl, todayIso, nowHM, combineDateHM } from "@/lib/datetime";
import { UploadCloud, Sparkles, X, Trash2, Loader2, ListChecks, Layers, CheckCircle2, Circle, AlertTriangle, ImageIcon } from "lucide-react";

const DEFAULT_MAX_DETAIL_SLOTS = 10;

// ----------------------------------------------------------------------------- 
// Slot — a droppable/pickable image square. If `variant="list"` it's larger.
// -----------------------------------------------------------------------------
function Slot({ variant = "detail", index, slot, onPick, onClear, onFocus, maxDetails }) {
  const inputRef = useRef(null);
  const isList = variant === "list";
  const size = isList ? "aspect-[16/10] min-h-[180px]" : "aspect-square min-h-[110px]";
  const label = isList
    ? `Capture de la liste screener (top ${maxDetails} pré-market) — obligatoire`
    : `Fiche détaillée #${index + 1}`;

  return (
    <div
      className={`relative rounded-lg border ${slot.file ? "border-line-strong" : "border-dashed border-line-strong hover:border-invest/50"} bg-input transition-colors overflow-hidden ${size}`}
      data-testid={`slot-${variant}-${isList ? "list" : index}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
      />
      {!slot.file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-muted-2 hover:text-app hover:bg-card-2 transition-colors p-3"
        >
          {isList ? <ListChecks size={22} /> : <ImageIcon size={18} />}
          <div className={`${isList ? "text-xs" : "text-[10px]"} text-center leading-tight`}>{label}</div>
          {isList && <div className="text-[10px] text-muted-1">clic pour ajouter</div>}
        </button>
      ) : (
        <>
          <img
            src={slot.imagePreview}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <ExtractionOverlay open={slot.loading} />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-1 p-1.5 bg-gradient-to-b from-black/60 to-transparent">
            <span className={`text-[10px] font-mono-num uppercase tracking-widest ${isList ? "bg-invest/20 text-invest border border-invest/30" : "bg-black/40 text-white border border-white/20"} rounded-full px-2 py-0.5`}>
              {isList ? "Liste" : `Fiche #${index + 1}`}
            </span>
            <button
              type="button"
              onClick={onClear}
              className="text-white/80 hover:text-white p-1"
              title="Retirer"
              data-testid={`slot-clear-${variant}-${isList ? "list" : index}`}
            >
              <X size={12} />
            </button>
          </div>
          {slot.error && (
            <div className="absolute inset-x-0 bottom-0 bg-ignore/85 text-white text-[10px] px-2 py-1 flex items-center gap-1">
              <AlertTriangle size={10} /> {slot.error}
            </div>
          )}
          {slot.extracted && !slot.error && (
            <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[10px] px-2 py-1 truncate">
              {slot.extracted}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Merge list rows and detail objects by symbol.
// -----------------------------------------------------------------------------
function mergeExtractions(listRows, detailObjs) {
  const bySymbol = {};
  for (const r of (listRows || [])) {
    const sym = String(r.symbol || "").toUpperCase().trim();
    if (!sym) continue;
    bySymbol[sym] = {
      symbol: sym,
      name: r.name || "",
      rank: r.rank ?? null,
      premarket_percent: r.percent_change ?? null,
      list_price: r.price ?? null,
      capture_price: r.price ?? null,
      criteria: {},
      from_list: true,
      from_detail: false,
      validated: false,
      saved: false,
      error: null,
    };
  }
  for (const d of (detailObjs || [])) {
    const sym = String(d.symbol || "").toUpperCase().trim();
    if (!sym) continue;
    if (!bySymbol[sym]) {
      bySymbol[sym] = {
        symbol: sym,
        name: d.name || "",
        rank: null,
        premarket_percent: null,
        capture_price: d.price ?? null,
        criteria: {},
        from_list: false,
        from_detail: true,
        validated: false,
        saved: false,
        error: null,
        orphan: true,
      };
    } else {
      bySymbol[sym].from_detail = true;
    }
    const row = bySymbol[sym];
    row.name = row.name || d.name || "";
    row.capture_price = d.price ?? row.capture_price ?? null;
    row.criteria = {
      volume: d.volume ?? null,
      avg_volume_3m: d.avg_volume_3m ?? null,
      shares_outstanding: d.shares_outstanding ?? null,
      open_price: d.open_price ?? null,
      prev_close: d.prev_close ?? null,
      pct_range: d.pct_range ?? null,
      market_cap: d.market_cap ?? null,
      pct_turnover: d.pct_turnover ?? null,
      news_category: d.news_category || "none",
    };
  }
  // Sort by rank (from list) — orphans (no rank) go to the end.
  return Object.values(bySymbol).sort((a, b) => {
    const ra = a.rank ?? 9999;
    const rb = b.rank ?? 9999;
    return ra - rb;
  });
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function BatchImport({ onSaved }) {
  const _initHM = nowHM();
  const [dt, setDt] = useState({ date: todayIso(), hour: _initHM.h, minute: _initHM.m });
  const [maxDetails, setMaxDetails] = useState(DEFAULT_MAX_DETAIL_SLOTS);
  const [listSlot, setListSlot] = useState({ file: null, imagePreview: null, loading: false, error: null, extracted: null, data: null });
  const [detailSlots, setDetailSlots] = useState(
    Array.from({ length: DEFAULT_MAX_DETAIL_SLOTS }, () => ({ file: null, imagePreview: null, loading: false, error: null, extracted: null, data: null }))
  );
  const [merging, setMerging] = useState(false);
  const [rows, setRows] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const multiInputRef = useRef(null);

  // Load user's max_tracked_per_day from settings and resize slots accordingly.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings").then((r) => {
      if (cancelled) return;
      const n = Math.max(1, Math.floor(Number(r.data?.max_tracked_per_day) || DEFAULT_MAX_DETAIL_SLOTS));
      setMaxDetails(n);
      setDetailSlots((prev) => {
        const keep = prev.slice(0, n);
        while (keep.length < n) {
          keep.push({ file: null, imagePreview: null, loading: false, error: null, extracted: null, data: null });
        }
        return keep;
      });
    }).catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, []);

  // --- slot helpers ------------------------------------------------------
  const putListImage = (fp) => {
    setListSlot({ file: fp, imagePreview: URL.createObjectURL(fp), loading: false, error: null, extracted: null, data: null });
    setRows(null);
  };
  const clearListSlot = () => {
    setListSlot({ file: null, imagePreview: null, loading: false, error: null, extracted: null, data: null });
    setRows(null);
  };
  const setDetailSlot = (i, updater) => {
    setDetailSlots((prev) => {
      const copy = prev.slice();
      copy[i] = typeof updater === "function" ? updater(copy[i]) : updater;
      return copy;
    });
  };
  const putDetailImage = (i, fp) => {
    setDetailSlot(i, { file: fp, imagePreview: URL.createObjectURL(fp), loading: false, error: null, extracted: null, data: null });
    setRows(null);
  };
  const clearDetailSlot = (i) => {
    setDetailSlot(i, { file: null, imagePreview: null, loading: false, error: null, extracted: null, data: null });
    setRows(null);
  };

  // Multi-select: first empty slot(s) get the images.
  // If list slot empty, first image → list; remaining → first empty detail slots.
  // Hard cap: N+1 images accepted (1 list + N details).
  const handleMultiSelect = (files) => {
    if (!files || files.length === 0) return;
    let arr = Array.from(files);
    const cap = maxDetails + 1;
    if (arr.length > cap) {
      toast.warning(
        `${arr.length - cap} image(s) ignorée(s) au-delà de la limite du jour`,
        { description: `Vous suivez ${maxDetails} action(s) par jour (Réglages). Sélection limitée à ${cap} images (1 liste + ${maxDetails} détails).` }
      );
      arr = arr.slice(0, cap);
    }
    let idx = 0;
    if (!listSlot.file && arr.length > 0) {
      putListImage(arr[idx]);
      idx++;
    }
    // remaining files → empty detail slots in order
    const emptyDetailIdx = [];
    detailSlots.forEach((s, i) => { if (!s.file) emptyDetailIdx.push(i); });
    while (idx < arr.length && emptyDetailIdx.length > 0) {
      const slotI = emptyDetailIdx.shift();
      putDetailImage(slotI, arr[idx]);
      idx++;
    }
    if (idx < arr.length) {
      toast.warning(`${arr.length - idx} image(s) ignorée(s)`, { description: `Slots pleins. Retirez d'abord des images ou réduisez la sélection.` });
    } else {
      toast.success(`${arr.length} image(s) placée(s) dans les slots`);
    }
  };

  // --- extraction --------------------------------------------------------
  const extractAll = async () => {
    if (!listSlot.file) {
      toast.error("Capture de la liste requise", { description: "Le rang de départ et le % pré-market viennent uniquement de la liste screener." });
      return;
    }
    setMerging(true);

    // List extraction
    setListSlot((s) => ({ ...s, loading: true, error: null, extracted: null }));
    const listPromise = (async () => {
      try {
        const fd = new FormData();
        fd.append("file", listSlot.file);
        const r = await api.post("/vision/extract-list", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const rows = r.data.rows || [];
        // Auto-fill rank sequentially from list order if missing
        const withRanks = rows.map((row, i) => ({
          ...row,
          rank: (row.rank != null && row.rank !== "" && Number(row.rank) > 0) ? Number(row.rank) : i + 1,
        }));
        // Enforce the daily cap: keep only the top-N by rank (respects the
        // user's `max_tracked_per_day` setting so we never propose to create
        // more stocks than the daily budget covers).
        const truncated = withRanks
          .slice()
          .sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999))
          .slice(0, maxDetails);
        const skipped = withRanks.length - truncated.length;
        setListSlot((s) => ({
          ...s,
          loading: false,
          extracted: skipped > 0 ? `${truncated.length}/${withRanks.length} titres (cap ${maxDetails})` : `${truncated.length} titres`,
          data: truncated,
        }));
        if (skipped > 0) {
          toast.info(`${skipped} action(s) ignorée(s) au-delà du top ${maxDetails}`, {
            description: "Ajustable dans Réglages → Actions suivies par jour.",
          });
        }
        return truncated;
      } catch (e) {
        const msg = e?.response?.data?.detail || "extraction échouée";
        setListSlot((s) => ({ ...s, loading: false, error: msg, data: null }));
        return [];
      }
    })();

    // Detail extractions in parallel
    const detailPromises = detailSlots.map((slot, i) => {
      if (!slot.file) return Promise.resolve(null);
      setDetailSlot(i, (s) => ({ ...s, loading: true, error: null, extracted: null }));
      return (async () => {
        try {
          const fd = new FormData();
          fd.append("file", slot.file);
          const r = await api.post("/vision/extract-detail", fd, { headers: { "Content-Type": "multipart/form-data" } });
          // Endpoint wraps payload as { detail: {...}, captured_at: ... }
          const data = r.data?.detail ?? r.data ?? {};
          setDetailSlot(i, (s) => ({ ...s, loading: false, extracted: data.symbol || "?", data }));
          return data;
        } catch (e) {
          const msg = e?.response?.data?.detail || "échec";
          setDetailSlot(i, (s) => ({ ...s, loading: false, error: msg, data: null }));
          return null;
        }
      })();
    });

    const [listData, ...detailResults] = await Promise.all([listPromise, ...detailPromises]);
    const detailsClean = detailResults.filter(Boolean);

    if (!listData || listData.length === 0) {
      toast.error("La liste n'a rien retourné");
      setMerging(false);
      return;
    }
    const merged = mergeExtractions(listData, detailsClean);
    // pre-mark orphans (details without list match) as unvalidated + error label
    setRows(merged);
    setMerging(false);
    const orphans = merged.filter((m) => m.orphan).length;
    toast.success(`${merged.length} titre(s) prêts à valider`, {
      description: orphans > 0 ? `${orphans} fiche(s) détaillée(s) sans correspondance dans la liste` : undefined,
    });
  };

  const patchRow = (i, patch) => setRows((prev) => {
    const c = prev.slice(); c[i] = { ...c[i], ...patch }; return c;
  });
  const patchCriteria = (i, k, v) => setRows((prev) => {
    const c = prev.slice(); c[i] = { ...c[i], criteria: { ...c[i].criteria, [k]: v } }; return c;
  });
  const removeRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i));

  const buildBody = (r) => ({
    symbol: String(r.symbol || "").toUpperCase(),
    name: r.name || null,
    trade_date: dt.date,
    rank_in_list: r.rank != null ? Number(r.rank) : null,
    criteria: {
      volume: numOrNull(r.criteria.volume),
      avg_volume_3m: numOrNull(r.criteria.avg_volume_3m),
      shares_outstanding: numOrNull(r.criteria.shares_outstanding),
      open_price: numOrNull(r.criteria.open_price),
      prev_close: numOrNull(r.criteria.prev_close),
      pct_range: numOrNull(r.criteria.pct_range),
      market_cap: numOrNull(r.criteria.market_cap),
      pct_turnover: numOrNull(r.criteria.pct_turnover),
      news_category: r.criteria.news_category || "none",
    },
    premarket: r.premarket_percent != null && r.premarket_percent !== ""
      ? { percent: Number(r.premarket_percent), timestamp: combineDateHM(dt.date, dt.hour, dt.minute) }
      : null,
    status: "test",
    capture_price: numOrNull(r.capture_price),
    entry_price: numOrNull(r.capture_price),
  });

  const validateAll = () => setRows((prev) => prev.map((r) => ({ ...r, validated: !r.orphan })));

  const saveOne = async (i) => {
    const r = rows[i];
    if (!r) return;
    try {
      const body = [buildBody(r)];
      const res = await api.post("/stocks/bulk", body);
      if ((res.data.duplicates || []).length > 0) {
        const ok = window.confirm(`${r.symbol} existe déjà pour ce jour. Écraser ?`);
        if (ok) {
          await api.post("/stocks/bulk?force_update=true", body);
          patchRow(i, { saved: true, error: null });
          toast.success(`${r.symbol} mis à jour`);
        }
      } else {
        patchRow(i, { saved: true, error: null });
        toast.success(`${r.symbol} ajouté`);
      }
    } catch (e) {
      patchRow(i, { error: e?.response?.data?.detail || String(e) });
      toast.error(`${r.symbol} — échec`);
    }
  };

  const saveAllValidated = async () => {
    if (!rows) return;
    const toSave = rows.filter((r) => r.validated && !r.saved);
    if (toSave.length === 0) {
      toast.error("Aucune ligne validée à enregistrer");
      return;
    }
    setSavingAll(true);
    try {
      const bodies = toSave.map(buildBody);
      const res = await api.post("/stocks/bulk", bodies);
      let inserted = res.data.inserted || 0;
      const dupes = res.data.duplicates || [];
      if (dupes.length > 0) {
        const symbolsList = dupes.map((d) => d.symbol).join(", ");
        const ok = window.confirm(`${dupes.length} doublon(s) : ${symbolsList}. Mettre à jour ?`);
        if (ok) {
          const dupeBodies = bodies.filter((b) => dupes.some((d) => d.symbol === b.symbol));
          const r2 = await api.post("/stocks/bulk?force_update=true", dupeBodies);
          toast.success(`${inserted} ajouté(s) · ${r2.data.updated} mis à jour`);
        } else {
          toast.success(`${inserted} ajouté(s) · ${dupes.length} doublon(s) ignoré(s)`);
        }
      } else {
        toast.success(`${inserted} action(s) ajoutée(s)`);
      }
      // Mark all as saved so user gets clear feedback
      setRows((prev) => prev.map((r) => (toSave.some((t) => t.symbol === r.symbol) ? { ...r, saved: true } : r)));
      onSaved?.();
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setSavingAll(false); }
  };

  const anyExtracted = useMemo(() => {
    return listSlot.data || detailSlots.some((s) => s.data);
  }, [listSlot.data, detailSlots]);

  const canExtract = !!listSlot.file && !merging;
  const filledDetailCount = detailSlots.filter((s) => s.file).length;
  const validatedCount = (rows || []).filter((r) => r.validated && !r.saved).length;
  const savedCount = (rows || []).filter((r) => r.saved).length;

  return (
    <div className="space-y-6">
      {/* Date & time */}
      <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-invest"><Layers size={14} /></div>
          <h3 className="font-display text-sm text-muted-1 uppercase tracking-widest">Journée & heure de la capture</h3>
        </div>
        <DateTimeControl
          date={dt.date} hour={dt.hour} minute={dt.minute}
          onChange={setDt} testIdPrefix="batch-datetime"
        />
      </div>

      {/* Slots */}
      <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display text-sm uppercase tracking-widest">Images à importer</div>
            <div className="text-[11px] text-muted-1 mt-0.5">
              1 capture de la liste screener (obligatoire) + jusqu'à {maxDetails} fiches détaillées.
              Fusion automatique par symbole.
            </div>
          </div>
          <div>
            <input
              ref={multiInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={(e) => { handleMultiSelect(e.target.files); e.target.value = ""; }}
              data-testid="batch-multiselect-input"
            />
            <Button
              variant="outline"
              onClick={() => multiInputRef.current?.click()}
              className="border-line-strong hover:bg-card-2 text-xs sm:text-sm"
              data-testid="batch-multiselect-btn"
            >
              <UploadCloud size={14} className="mr-2" /> Sélectionner plusieurs images
            </Button>
          </div>
        </div>

        {/* List slot (mandatory, large) */}
        <div className="max-w-md mx-auto">
          <Slot
            variant="list"
            slot={listSlot}
            onPick={putListImage}
            onClear={clearListSlot}
            maxDetails={maxDetails}
          />
        </div>

        {/* Detail slots grid */}
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-2 mb-2">
            <span>Fiches détaillées</span>
            <span data-testid="batch-slot-count">{filledDetailCount}/{maxDetails}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {detailSlots.map((slot, i) => (
              <Slot
                key={i}
                variant="detail"
                index={i}
                slot={slot}
                onPick={(fp) => putDetailImage(i, fp)}
                onClear={() => clearDetailSlot(i)}
                maxDetails={maxDetails}
              />
            ))}
          </div>
        </div>

        <Button
          onClick={extractAll}
          disabled={!canExtract}
          className="w-full h-11 btn-primary"
          data-testid="batch-extract-btn"
        >
          {merging
            ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyse en cours…</>
            : <><Sparkles size={14} className="mr-2" /> Analyser les images ({1 + filledDetailCount})</>}
        </Button>
      </div>

      {/* Merged preview grid */}
      {rows && (
        <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6 space-y-4 fade-in-up">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-display text-sm uppercase tracking-widest">Aperçu fusionné · {rows.length} titre(s)</h3>
              <div className="text-[11px] text-muted-1 mt-0.5">
                Rang &amp; %pré viennent uniquement de la liste. Les autres critères viennent des fiches détaillées.
                Valide chaque ligne (ou toutes d'un coup) avant d'enregistrer.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={validateAll}
                className="text-xs border-line-strong"
                data-testid="batch-validate-all"
              >Tout valider</Button>
              <Button
                onClick={saveAllValidated}
                disabled={savingAll || validatedCount === 0}
                className="btn-primary text-xs"
                data-testid="batch-save-all"
              >
                {savingAll ? "Enregistrement…" : `Enregistrer les ${validatedCount} validé(s)`}
              </Button>
            </div>
          </div>

          {/* Diagnostic panel */}
          {(() => {
            const listCount = (listSlot.data || []).length;
            const detailCount = detailSlots.filter((s) => s.data).length;
            const merged = rows.filter((r) => r.from_list && r.from_detail).length;
            const orphanCount = rows.filter((r) => r.orphan).length;
            const listOnly = rows.filter((r) => r.from_list && !r.from_detail).length;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="bg-input border border-line rounded px-3 py-2">
                  <div className="text-muted-2 uppercase tracking-widest text-[9px]">Liste</div>
                  <div className="font-mono-num text-app">{listCount} titre(s)</div>
                </div>
                <div className="bg-input border border-line rounded px-3 py-2">
                  <div className="text-muted-2 uppercase tracking-widest text-[9px]">Fiches</div>
                  <div className="font-mono-num text-app">{detailCount} analysée(s)</div>
                </div>
                <div className="bg-input border border-line rounded px-3 py-2">
                  <div className="text-muted-2 uppercase tracking-widest text-[9px]">Fusionnées</div>
                  <div className="font-mono-num text-invest">{merged} complète(s)</div>
                </div>
                <div className={`bg-input border rounded px-3 py-2 ${orphanCount > 0 ? "border-ignore/30" : "border-line"}`}>
                  <div className="text-muted-2 uppercase tracking-widest text-[9px]">Orphelines</div>
                  <div className={`font-mono-num ${orphanCount > 0 ? "text-ignore" : "text-muted-2"}`}>{orphanCount}</div>
                </div>
                {listOnly > 0 && (
                  <div className="col-span-2 sm:col-span-4 text-[10px] text-monitor bg-monitor/10 border border-monitor/25 rounded px-3 py-2">
                    ⚠ {listOnly} titre(s) de la liste sans fiche détaillée correspondante — critères manquants (le score sera bas).
                  </div>
                )}
              </div>
            );
          })()}

          {savedCount > 0 && (
            <div className="text-[11px] text-invest bg-invest/10 border border-invest/25 rounded px-3 py-2">
              ✓ {savedCount} action(s) enregistrée(s) en base.
            </div>
          )}

          <div className="space-y-2">
            {rows.map((r, i) => (
              <RowEditor
                key={i}
                row={r}
                onChange={(patch) => patchRow(i, patch)}
                onChangeCriteria={(k, v) => patchCriteria(i, k, v)}
                onRemove={() => removeRow(i)}
                onSave={() => saveOne(i)}
                onValidate={() => patchRow(i, { validated: !r.validated })}
              />
            ))}
          </div>
        </div>
      )}

      {!rows && anyExtracted && !merging && (
        <div className="text-[11px] text-muted-1">Extraction en attente — clique sur <b>Analyser les images</b> ci-dessus.</div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// RowEditor — one merged row with inline editing + per-row validate/save
// -----------------------------------------------------------------------------
function RowEditor({ row, onChange, onChangeCriteria, onRemove, onSave, onValidate }) {
  const badgeCls = row.saved
    ? "bg-invest/15 text-invest border-invest/30"
    : row.validated
    ? "bg-monitor/15 text-monitor border-monitor/30"
    : row.orphan
    ? "bg-ignore/15 text-ignore border-ignore/30"
    : "bg-input text-muted-2 border-line-strong";
  const badgeLabel = row.saved ? "Enregistré" : row.validated ? "Validé" : row.orphan ? "Hors liste" : "À valider";
  return (
    <div className={`rounded-lg border ${row.error ? "border-ignore/40" : "border-line"} bg-input/40 p-3 space-y-3`} data-testid={`row-${row.symbol}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-mono-num text-xs text-muted-2 w-8">
            {row.rank != null ? `#${row.rank}` : "—"}
          </span>
          <span className="font-display font-semibold text-app">{row.symbol}</span>
          <span className="text-xs text-muted-1 truncate max-w-[180px]">{row.name}</span>
          <span className={`text-[10px] font-mono-num uppercase tracking-widest border rounded-full px-2 py-0.5 ${badgeCls}`}>
            {badgeLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {!row.saved && (
            <>
              <button
                type="button"
                onClick={onValidate}
                className={`p-1.5 rounded hover:bg-card-2 ${row.validated ? "text-invest" : "text-muted-2"}`}
                title={row.validated ? "Retirer la validation" : "Valider"}
                data-testid={`row-validate-${row.symbol}`}
              >
                {row.validated ? <CheckCircle2 size={16} /> : <Circle size={16} />}
              </button>
              <button
                type="button"
                onClick={onSave}
                className="text-xs px-2 py-1 rounded border border-line-strong hover:bg-card-2 text-app"
                data-testid={`row-save-${row.symbol}`}
              >Enregistrer</button>
            </>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-2 hover:text-ignore p-1.5"
            title="Retirer de la sélection"
            data-testid={`row-remove-${row.symbol}`}
          ><Trash2 size={14} /></button>
        </div>
      </div>

      {row.error && (
        <div className="text-[11px] text-ignore bg-ignore/10 border border-ignore/25 rounded px-2 py-1">
          {row.error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MiniField label="Rang" value={row.rank ?? ""} onChange={(v) => onChange({ rank: v === "" ? null : Number(v) })} testId={`f-rank-${row.symbol}`} />
        <MiniField label="% Pré" value={row.premarket_percent ?? ""} onChange={(v) => onChange({ premarket_percent: v })} testId={`f-pre-${row.symbol}`} />
        <MiniField label="Prix $" value={row.capture_price ?? ""} onChange={(v) => onChange({ capture_price: v === "" ? null : Number(v) })} testId={`f-price-${row.symbol}`} />
        <MiniField label="Volume" value={row.criteria.volume ?? ""} onChange={(v) => onChangeCriteria("volume", numOrNull(v))} testId={`f-vol-${row.symbol}`} />
        <MiniField label="Vol. moy" value={row.criteria.avg_volume_3m ?? ""} onChange={(v) => onChangeCriteria("avg_volume_3m", numOrNull(v))} testId={`f-avol-${row.symbol}`} />
        <MiniField label="Flottant" value={row.criteria.shares_outstanding ?? ""} onChange={(v) => onChangeCriteria("shares_outstanding", numOrNull(v))} testId={`f-float-${row.symbol}`} />
        <MiniField label="Cap. bours." value={row.criteria.market_cap ?? ""} onChange={(v) => onChangeCriteria("market_cap", numOrNull(v))} testId={`f-mcap-${row.symbol}`} />
        <MiniField label="% fourch." value={row.criteria.pct_range ?? ""} onChange={(v) => onChangeCriteria("pct_range", numOrNull(v))} testId={`f-range-${row.symbol}`} />
        <MiniField label="% roul." value={row.criteria.pct_turnover ?? ""} onChange={(v) => onChangeCriteria("pct_turnover", numOrNull(v))} testId={`f-turn-${row.symbol}`} />
        <MiniField label="Ouverture" value={row.criteria.open_price ?? ""} onChange={(v) => onChangeCriteria("open_price", numOrNull(v))} testId={`f-open-${row.symbol}`} />
        <MiniField label="Prev close" value={row.criteria.prev_close ?? ""} onChange={(v) => onChangeCriteria("prev_close", numOrNull(v))} testId={`f-prev-${row.symbol}`} />
        <MiniField label="News" value={row.criteria.news_category ?? "none"} onChange={(v) => onChangeCriteria("news_category", v || "none")} testId={`f-news-${row.symbol}`} />
      </div>

      <div className="flex items-center gap-3 text-[10px] text-muted-2">
        <span className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${row.from_list ? "bg-invest" : "bg-muted-2/40"}`} /> liste
        </span>
        <span className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${row.from_detail ? "bg-invest" : "bg-muted-2/40"}`} /> fiche détail
        </span>
      </div>
    </div>
  );
}

function MiniField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[9px] uppercase tracking-widest text-muted-2">{label}</Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 mt-1 bg-input border-line-strong text-xs font-mono-num"
        data-testid={testId}
      />
    </div>
  );
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;   // preserve string if unparseable (news_category etc.)
}
